import { BaseClient } from '../client/BaseClient';
import { Status as StatusType, StatusUpdate } from '../types';

export class Status {
  constructor(private client: BaseClient) {}

  /**
   * Get all status updates
   * @param tel - Phone number
   */
  async list(tel: string): Promise<StatusType[]> {
    const response = await this.client['get']<{
      success: boolean;
      statuses: StatusType[];
    }>(`/api/status/${encodeURIComponent(tel)}`);
    
    return response.statuses;
  }

  /**
   * Get status updates from a specific contact
   * @param tel - Phone number
   * @param contactId - Contact ID
   */
  async getContactStatus(
    tel: string,
    contactId: string
  ): Promise<StatusType[]> {
    const response = await this.client['get']<{
      success: boolean;
      statuses: StatusType[];
    }>(`/api/status/${encodeURIComponent(tel)}/${encodeURIComponent(contactId)}`);
    
    return response.statuses;
  }

  /**
   * Post a new status update
   * @param data - Status update data
   */
  async post(data: StatusUpdate): Promise<{ id: string }> {
    const response = await this.client['post']<{
      success: boolean;
      id: string;
    }>('/api/status', data);
    
    return { id: response.id };
  }

  /**
   * Post a text status
   * @param tel - Phone number
   * @param content - Status text
   * @param backgroundColor - Background color (hex)
   * @param font - Font style (0-5)
   */
  async postText(
    tel: string,
    content: string,
    backgroundColor?: string,
    font?: number
  ): Promise<{ id: string }> {
    return this.post({
      tel,
      type: 'text',
      content,
      backgroundColor,
      font,
    });
  }

  /**
   * Post an image status
   * @param tel - Phone number
   * @param media - Base64 encoded image
   * @param caption - Optional caption
   */
  async postImage(
    tel: string,
    media: string,
    caption?: string
  ): Promise<{ id: string }> {
    return this.post({
      tel,
      type: 'image',
      media,
      caption,
    });
  }

  /**
   * Post a video status
   * @param tel - Phone number
   * @param media - Base64 encoded video
   * @param caption - Optional caption
   */
  async postVideo(
    tel: string,
    media: string,
    caption?: string
  ): Promise<{ id: string }> {
    return this.post({
      tel,
      type: 'video',
      media,
      caption,
    });
  }

  /**
   * Delete a status update
   * @param tel - Phone number
   * @param statusId - Status ID to delete
   */
  async delete(
    tel: string,
    statusId: string
  ): Promise<{ success: boolean }> {
    return this.client['delete']<{ success: boolean }>(
      `/api/status/${encodeURIComponent(tel)}/${statusId}`
    );
  }

  /**
   * Mark status as seen
   * @param tel - Phone number
   * @param statusId - Status ID
   */
  async markSeen(
    tel: string,
    statusId: string
  ): Promise<{ success: boolean }> {
    return this.client['post']<{ success: boolean }>('/api/status/seen', {
      tel,
      statusId,
    });
  }

  /**
   * Get status privacy settings
   * @param tel - Phone number
   */
  async getPrivacySettings(tel: string): Promise<{
    blacklist: string[];
    whitelist: string[];
    mode: 'all' | 'contacts' | 'contact_blacklist' | 'selected';
  }> {
    const response = await this.client['get']<{
      success: boolean;
      privacy: {
        blacklist: string[];
        whitelist: string[];
        mode: 'all' | 'contacts' | 'contact_blacklist' | 'selected';
      };
    }>(`/api/status/${encodeURIComponent(tel)}/privacy`);
    
    return response.privacy;
  }

  /**
   * Update status privacy settings
   * @param tel - Phone number
   * @param settings - Privacy settings
   */
  async updatePrivacySettings(
    tel: string,
    settings: {
      mode?: 'all' | 'contacts' | 'contact_blacklist' | 'selected';
      blacklist?: string[];
      whitelist?: string[];
    }
  ): Promise<{ success: boolean }> {
    return this.client['post']<{ success: boolean }>(
      `/api/status/${encodeURIComponent(tel)}/privacy`,
      settings
    );
  }

  /**
   * Download status media
   * @param tel - Phone number
   * @param statusId - Status ID
   */
  async downloadMedia(
    tel: string,
    statusId: string
  ): Promise<{
    data: string;
    mimetype: string;
    filename: string;
  }> {
    const response = await this.client['get']<{
      success: boolean;
      data: string;
      mimetype: string;
      filename: string;
    }>(`/api/status/${encodeURIComponent(tel)}/${statusId}/media`);
    
    return {
      data: response.data,
      mimetype: response.mimetype,
      filename: response.filename,
    };
  }

  /**
   * Get who viewed a status
   * @param tel - Phone number
   * @param statusId - Status ID
   */
  async getViewers(
    tel: string,
    statusId: string
  ): Promise<string[]> {
    const response = await this.client['get']<{
      success: boolean;
      viewers: string[];
    }>(`/api/status/${encodeURIComponent(tel)}/${statusId}/viewers`);
    
    return response.viewers;
  }
}